import React, { useState, useEffect, useCallback } from 'react';
import { dispatchAPI } from '../services/api';
import DispatchCard from '../components/DispatchCard';
import Map from '../components/Map';

const Dashboard = () => {
  const [dispatches, setDispatches] = useState([]);
  const [selectedDispatch, setSelectedDispatch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [mapCenter, setMapCenter] = useState({ lat: 40.7128, lng: -74.0060 });
  const [markers, setMarkers] = useState([]);

  const fetchDispatches = useCallback(async () => {
    try {
      setLoading(true);
      const params = filter !== 'all' ? { status: filter } : {};
      const response = await dispatchAPI.getDispatches(params);
      setDispatches(response.data.data);
    } catch (error) {
      console.error('Error fetching dispatches:', error);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchDispatches();
  }, [fetchDispatches]);

  useEffect(() => {
    if (selectedDispatch) {
      updateMapForDispatch(selectedDispatch);
    }
  }, [selectedDispatch]);

  const updateMapForDispatch = (dispatch) => {
    const newMarkers = [];

    // Add pickup marker
    newMarkers.push({
      position: dispatch.pickupLocation.coordinates,
      title: 'Pickup Location',
      label: 'P',
      icon: {
        path: window.google.maps.SymbolPath.CIRCLE,
        scale: 10,
        fillColor: '#10b981',
        fillOpacity: 1,
        strokeColor: 'white',
        strokeWeight: 2
      },
      infoWindow: `<div><strong>Pickup</strong><br/>${dispatch.pickupLocation.address}</div>`
    });

    // Add delivery marker
    newMarkers.push({
      position: dispatch.deliveryLocation.coordinates,
      title: 'Delivery Location',
      label: 'D',
      icon: {
        path: window.google.maps.SymbolPath.CIRCLE,
        scale: 10,
        fillColor: '#ef4444',
        fillOpacity: 1,
        strokeColor: 'white',
        strokeWeight: 2
      },
      infoWindow: `<div><strong>Delivery</strong><br/>${dispatch.deliveryLocation.address}</div>`
    });

    // Add current location marker if available
    if (dispatch.currentLocation && dispatch.currentLocation.coordinates) {
      newMarkers.push({
        position: dispatch.currentLocation.coordinates,
        title: 'Current Location',
        icon: {
          path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
          scale: 6,
          fillColor: '#3b82f6',
          fillOpacity: 1,
          strokeColor: 'white',
          strokeWeight: 2
        },
        infoWindow: '<div><strong>Driver Location</strong></div>'
      });
      setMapCenter(dispatch.currentLocation.coordinates);
    } else {
      setMapCenter(dispatch.pickupLocation.coordinates);
    }

    setMarkers(newMarkers);
  };

  const handleViewDetails = (dispatch) => {
    setSelectedDispatch(dispatch);
  };

  return (
    <div style={{ display: 'flex', height: 'calc(100vh - 60px)', backgroundColor: '#f9fafb' }}>
      {/* Left Panel - Dispatch List */}
      <div style={{ width: '400px', borderRight: '1px solid #e5e7eb', overflow: 'auto', padding: '20px' }}>
        <h2 style={{ marginTop: 0 }}>Dispatches</h2>
        
        {/* Filter Buttons */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' }}>
          {['all', 'pending', 'assigned', 'in_progress', 'delivered'].map((status) => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              style={{
                padding: '8px 16px',
                borderRadius: '6px',
                border: '1px solid #d1d5db',
                backgroundColor: filter === status ? '#3b82f6' : 'white',
                color: filter === status ? 'white' : '#374151',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              {status.replace('_', ' ').toUpperCase()}
            </button>
          ))}
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
            Loading...
          </div>
        ) : dispatches.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
            No dispatches found
          </div>
        ) : (
          dispatches.map((dispatch) => (
            <DispatchCard
              key={dispatch._id}
              dispatch={dispatch}
              onViewDetails={handleViewDetails}
            />
          ))
        )}
      </div>

      {/* Right Panel - Map */}
      <div style={{ flex: 1, position: 'relative' }}>
        {selectedDispatch && (
          <div style={{
            position: 'absolute',
            top: '20px',
            left: '20px',
            right: '20px',
            backgroundColor: 'white',
            padding: '16px',
            borderRadius: '8px',
            boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
            zIndex: 1000
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '18px' }}>{selectedDispatch.requestId}</h3>
                <p style={{ margin: '4px 0', color: '#6b7280', fontSize: '14px' }}>
                  Status: {selectedDispatch.status.replace('_', ' ')}
                </p>
              </div>
              <button
                onClick={() => setSelectedDispatch(null)}
                style={{
                  padding: '8px 16px',
                  borderRadius: '6px',
                  border: '1px solid #d1d5db',
                  backgroundColor: 'white',
                  cursor: 'pointer'
                }}
              >
                Close
              </button>
            </div>
          </div>
        )}
        <Map
          center={mapCenter}
          markers={markers}
          zoom={13}
        />
      </div>
    </div>
  );
};

export default Dashboard;
